import React, {useEffect, useRef, useState} from "react";
import {chatStart, chatSend} from "../../services/api";
import "./ChatWidget.scss";

/**
 * Floating AI assistant (bottom-right).
 *
 * Talks ONLY to our own backend (api/chat.php), which proxies to Salesforce
 * Agentforce server-side — no Salesforce credentials or tokens ever reach
 * the browser.
 *
 * Degrades gracefully:
 *   - backend not configured yet (503)  -> friendly "use the contact form" note
 *   - Agentforce session expired (410)  -> transparently restarts the session
 *   - network/API failure               -> inline error, chat stays usable
 */

const SUGGESTIONS = [
  "What Salesforce architecture work has Rashid done?",
  "Tell me about his Agentforce experience",
  "Is Rashid available for consulting?"
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: "user"|"bot"|"error", text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const sessionRef = useRef(null); // {id, seq}
  const startedRef = useRef(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to the newest message.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, busy, open]);

  // Start the Agentforce session lazily, on first open only.
  useEffect(() => {
    if (!open || startedRef.current) return;
    startedRef.current = true;
    setBusy(true);
    chatStart()
      .then(res => {
        sessionRef.current = {id: res.sessionId, seq: 0};
        setMessages([{role: "bot", text: res.message}]);
      })
      .catch(e => {
        if (e.status === 503) {
          setUnavailable(true);
          setMessages([
            {
              role: "bot",
              text:
                "The AI assistant is offline right now. You can reach Rashid " +
                "directly through the contact section — he replies personally."
            }
          ]);
        } else {
          startedRef.current = false; // allow retry on next open
          setMessages([
            {role: "error", text: "Couldn't reach the assistant. Try again in a moment."}
          ]);
        }
      })
      .finally(() => setBusy(false));
  }, [open]);

  const send = async textArg => {
    const text = (textArg ?? input).trim();
    if (!text || busy || unavailable || !sessionRef.current) return;

    setInput("");
    setMessages(m => [...m, {role: "user", text}]);
    setBusy(true);

    const attempt = async () => {
      sessionRef.current.seq += 1;
      return chatSend(sessionRef.current.id, text, sessionRef.current.seq);
    };

    try {
      let res;
      try {
        res = await attempt();
      } catch (e) {
        if (e.status === 410) {
          // Agentforce session timed out — restart transparently and resend.
          const fresh = await chatStart();
          sessionRef.current = {id: fresh.sessionId, seq: 0};
          res = await attempt();
        } else {
          throw e;
        }
      }
      setMessages(m => [...m, {role: "bot", text: res.message}]);
    } catch (e) {
      const text429 = e.status === 429;
      setMessages(m => [
        ...m,
        {
          role: "error",
          text: text429
            ? "Easy there — a few messages per minute, please. Try again shortly."
            : "That didn't go through. Please try again."
        }
      ]);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const onKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="chat-widget">
      {open && (
        <div className="chat-widget__panel" role="dialog" aria-label="AI assistant chat">
          <div className="chat-widget__header">
            <div>
              <div className="chat-widget__title">Rashid's AI Assistant</div>
              <div className="chat-widget__subtitle">Powered by Salesforce Agentforce</div>
            </div>
            <button
              type="button"
              className="chat-widget__close"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="chat-widget__messages" ref={listRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-widget__msg chat-widget__msg--${m.role}`}>
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="chat-widget__msg chat-widget__msg--bot chat-widget__typing">
                <span />
                <span />
                <span />
              </div>
            )}
            {!busy && !unavailable && messages.length === 1 && (
              <div className="chat-widget__suggestions">
                {SUGGESTIONS.map(s => (
                  <button key={s} type="button" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="chat-widget__inputRow">
            <textarea
              ref={inputRef}
              rows={1}
              maxLength={1000}
              placeholder={unavailable ? "Assistant offline" : "Ask about my work…"}
              value={input}
              disabled={unavailable}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Chat message"
            />
            <button
              type="button"
              className="chat-widget__send"
              disabled={busy || unavailable || !input.trim()}
              onClick={() => send()}
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`chat-widget__launcher ${open ? "is-open" : ""}`}
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        onClick={() => setOpen(o => !o)}
      >
        {open ? "×" : "💬"}
      </button>
    </div>
  );
}
